// Constants
import { DynamicFilterField } from "@ms/planner-models/lib/local/constants/FilterConstants";
import { ClientType } from "@ms/uno-constants/lib/local/AppConstants";
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { FloodgateActivity } from "@ms/uno-constants/lib/local/FloodgateConstants";
import { NewMyTasksExperienceLocalStorageKey } from "@ms/uno-constants/lib/local/PersistenceConstants";
import { DefaultViewTypeForPersonalViews, SubRouteKeys, ViewType } from "@ms/uno-constants/lib/local/RoutingConstants";
import { GridViewName } from "@ms/uno-constants/lib/local/TaskGridConstants";
import { ElementIds, ViewElementIds } from "@ms/uno-constants/lib/local/UIConstants";
import { TryPremiumEntryPoint } from "@ms/uno-constants/lib/local/UpsellConstants";
import { ViewDisplayState, ViewName } from "@ms/uno-constants/lib/local/ViewConstants";
import { DefaultMyTasksViewPivotType, MyTasksViewPivotType } from "@ms/uno-constants/lib/local/ViewPivots";
import { MessageBarGroup, mergeClasses, Switch } from "@fluentui/react-components";
import { HeaderOverflowMenuContainer } from "@ms/planner-container-controls/lib/local/containers/headerOverflowMenu/HeaderOverflowMenuContainer";
import { LazyMyTasksListViewContainer } from "@ms/planner-container-controls/lib/local/containers/myTasksListViewContainer/LazyMyTasksListViewContainer";
import { UpsellTrialButtonContainer } from "@ms/planner-container-controls/lib/local/containers/upsellTrialButton/UpsellTrialButtonContainer";
import { LazyUserTasksBoardView } from "@ms/planner-container-controls/lib/local/views/board/LazyUserTasksBoardView";
import { LazyCanvasUserTasksGridView } from "@ms/planner-container-controls/lib/local/views/grid/LazyCanvasUserTasksGridView";
import { LazyUserTasksGridView } from "@ms/planner-container-controls/lib/local/views/grid/LazyUserTasksGridView";
import { MyDayMyTaskTaskCardPlaceHolders, ResponsiveHeaderWidths } from "@ms/planner-generic-controls/lib/local/constants/StyleConstants";
import { LazyEmptyStateView } from "@ms/planner-generic-controls/lib/local/emptyStateView/EmptyStateViewLazyLoader";
import { ViewTransformBarLayout } from "@ms/planner-generic-controls/lib/local/viewTransformBarLayout/ViewTransformBarLayout";
import { PageLayout } from "@ms/planner-presentational-controls/lib/local/pageLayout/PageLayout";
import { NARROW_CONTENT_MARKER } from "@ms/planner-presentational-controls/lib/local/myTasksListView/MyTasksListView.styles";
import { TaskBoardViewPlaceHolder } from "@ms/planner-presentational-controls/lib/local/uxPlaceholders/taskBoardView/TaskBoardViewPlaceHolder";
import { UserViewHeader } from "../../presentational/userViewHeader/UserViewHeader";
import { MyTasksDynamicFilterMessageBar } from "./MyTasksDynamicFilterMessageBar";
import { MyTasksStarredMessageBar } from "./MyTasksStarredMessageBar";
import { ViewActionBarContainer } from "./viewTransform/ViewActionBarContainer";
import { ViewNavigationBarContainer } from "./viewTransform/ViewNavigationBarContainer";
// Resources
import { MyTasksRegular } from "@ms/planner-generic-controls/lib/local/icons";
import { loadTranslations, Strings } from "../../../utilities/CultureUtilities";
// Action Creators
import { ActionCreator } from "@ms/planner-actioncreators/lib/local/actioncreators/ActionCreator";
// Stores
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
// Models
import { PrivateTasksVirtualGroupKey } from "@ms/uno-constants/lib/local/GroupingConstants";
import { TaskAccessContext } from "@ms/planner-models/lib/local/client/planner/Enumerations";
// Utilities
import { useActionCreators } from "@ms/planner-container-controls/lib/local/hooks/useActionCreators";
import { useConfigProvider } from "@ms/planner-container-controls/lib/local/hooks/useConfigProvider";
import { useRenderTracker } from "@ms/planner-container-controls/lib/local/hooks/useRenderTracker";
import { useStores } from "@ms/planner-container-controls/lib/local/hooks/useStores";
import { getPrimaryViewContentWidth } from "@ms/planner-container-controls/lib/local/utilities/TaskEditorPanelLayoutUtilities";
import { shouldDisplayLoadingIndicator } from "@ms/planner-generic-controls/lib/local/utilities/loadingIndicator/LoadingIndicatorUtilities";
import { useLoggers } from "@ms/planner-presentational-controls/lib/local/utilities/hooks/useLoggers";
import { usePlannerTheme } from "@ms/planner-presentational-controls/lib/local/utilities/hooks/usePlannerTheme";
import { extractMyTasksRoutingDetailsFromSubRoute } from "@ms/uno-routing/lib/local/utilities/RouteUtilities";
import * as React from "react";
import { Suspense, useCallback, useEffect } from "react";
import { getEmptyStateProps } from "../../../utilities/MyTasksPageUtilities";
import { calculateMyTasksRenderState } from "./MyTasksPage.perf";
import { useMyTasksPageStyles } from "./MyTasksPage.styles";
import { useEngagementGate } from "./useEngagementGate";
// Telemetry
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
const componentName = ViewName.MyTasksPage;
/**
 * My Tasks Page component
 * Todo 2735703: MyTasksPage Performance Tracker
 */ export const MyTasksPage = ()=>{
    const styles = useMyTasksPageStyles();
    const configProvider = useConfigProvider();
    const { traceLogger, engagementLogger } = useLoggers();
    const [routeStore, appContextStore, planStore, viewLifecycleStore, taskStore, viewStore, modalStore, permissionsStore] = useStores([
        Store.Route,
        Store.AppContext,
        Store.Plan,
        Store.ViewLifecycle,
        Store.Tasks,
        Store.View,
        Store.Modal,
        Store.Permissions,
        Store.PinnedTasks
    ]);
    const [ocvActionCreator, routeActionCreator, viewTransformsActionCreator] = useActionCreators([
        ActionCreator.OCV,
        ActionCreator.Routing,
        ActionCreator.ViewTransforms
    ]);
    const theme = usePlannerTheme();
    // Get current pivot and view type from route
    const subRouteType = routeStore.getCurrentSubRouteType();
    const subRoute = routeStore.getCurrentSubRoute();
    const routingDetails = extractMyTasksRoutingDetailsFromSubRoute(subRoute, subRouteType);
    const viewType = routingDetails?.viewType || DefaultViewTypeForPersonalViews;
    const routedPivot = routingDetails?.pivotType || DefaultMyTasksViewPivotType;
    // The URL is the source of truth: /view/list = new experience, /view/board|grid = old.
    // Bootstrap (getInitialSubRoute) reads the old localStorage key to set the initial route.
    // Once confirmed here with the flight check, clean up the key after commit.
    const isNewMyTasksFlightEnabled = configProvider().flights.EnableNewMyTasksExperience;
    const isStarredExperienceEnabled = configProvider().flights.EnableStarredExperience;
    const pivot = !isStarredExperienceEnabled && routedPivot === MyTasksViewPivotType.Important ? DefaultMyTasksViewPivotType : routedPivot;
    const isListView = viewType === ViewType.List;
    const shouldUseNewListView = isNewMyTasksFlightEnabled && isListView;
    const hasEverBeenListViewInSessionRef = React.useRef(isListView);
    // Clean up the legacy localStorage key when the flight is confirmed active
    // and the user is already on the list view (e.g., via bootstrap migration).
    // This runs at most once: after the key is removed, removeItem is a no-op.
    if (shouldUseNewListView) {
        try {
            localStorage.removeItem(NewMyTasksExperienceLocalStorageKey);
        } catch  {
        // localStorage may be unavailable
        }
    }
    const currentTaskId = routingDetails?.task;
    // Gate the opt-out survey behind a minimum engagement threshold (time OR interaction).
    const { containerRef: listViewContainerRef, hasEngaged } = useEngagementGate(isListView);
    // The view content width, which already excludes the left nav and the Copilot sidecar, is what
    // decides whether the page scrolls as a whole. A viewport media query would misjudge this: the
    // content can be narrow while the window is wide.
    const isNarrowContent = getPrimaryViewContentWidth({
        viewStore,
        modalStore,
        taskStore,
        planStore,
        permissionsStore
    }, configProvider) <= ResponsiveHeaderWidths.maxWidth640;
    const handleToggleNewExperience = useCallback((_, data)=>{
        if (data.checked) {
            hasEverBeenListViewInSessionRef.current = true;
        }
        engagementLogger.logEngagement(componentName, data.checked ? EngagementTelemetryConstants.MyTasksEnableNewExperience : EngagementTelemetryConstants.MyTasksDisableNewExperience);
        // Trigger opt-out survey exclusively for the new My Tasks experience:
        // only when the user disables it AND has engaged (time/interaction threshold).
        if (isNewMyTasksFlightEnabled && !data.checked && hasEngaged()) {
            ocvActionCreator.showNewMyTasksOptOutSurvey();
        }
        // Deactivate the dynamic filter when toggling away from the new experience
        // so it doesn't affect the old board/grid view at the store level.
        if (!data.checked) {
            const dynamicFilterViewId = {
                name: ViewName.MyTasksPage,
                partitionKey: pivot
            };
            const currentFilter = viewStore.getCurrentTaskFilter(dynamicFilterViewId);
            const activeDynamicFilter = currentFilter?.filters?.some((f)=>f.field === DynamicFilterField && f.active);
            if (activeDynamicFilter && currentFilter) {
                viewTransformsActionCreator.setTaskFilter(dynamicFilterViewId, currentFilter.setFilterActive(DynamicFilterField, false));
            }
            // Collapse the Catalyst sidecar since the DAB is not available in old views.
            // Keep the instance alive so it restores when the user reopens via DAB.
            const sidecarState = viewStore.getCopilotSidecarState();
            if (sidecarState.open && !sidecarState.collapsed) {
                viewTransformsActionCreator.setCopilotSidecarState({
                    ...sidecarState,
                    collapsed: true
                });
            }
        }
        const targetViewType = data.checked ? ViewType.List : DefaultViewTypeForPersonalViews;
        const taskSegment = currentTaskId ? [
            SubRouteKeys.Task,
            currentTaskId
        ] : [];
        routeActionCreator.pushSubRoute([
            pivot,
            SubRouteKeys.View,
            targetViewType,
            ...taskSegment
        ]);
    }, [
        pivot,
        routeActionCreator,
        currentTaskId,
        engagementLogger,
        ocvActionCreator,
        hasEngaged,
        isNewMyTasksFlightEnabled,
        viewTransformsActionCreator,
        viewStore
    ]);
    let mustShowPlanId = null;
    const priorityPlanIds = [];
    // Which plan backs Private Tasks is not this page's concern: it names the group and the board maps that
    // key to whichever plan currently backs it.
    const hasPrivateTasksPlan = planStore.hasDefaultPlan();
    const flaggedEmailPlanId = planStore.getFlaggedEmailPlanId();
    switch(pivot){
        case MyTasksViewPivotType.All:
            if (hasPrivateTasksPlan) {
                priorityPlanIds.push(PrivateTasksVirtualGroupKey);
                mustShowPlanId = PrivateTasksVirtualGroupKey;
            }
            if (flaggedEmailPlanId) {
                priorityPlanIds.push(flaggedEmailPlanId);
            }
            break;
        case MyTasksViewPivotType.PrivateTasks:
            if (hasPrivateTasksPlan) {
                mustShowPlanId = PrivateTasksVirtualGroupKey;
            }
            break;
        case MyTasksViewPivotType.FlaggedEmails:
        case MyTasksViewPivotType.AssignedToMe:
        default:
            break;
    }
    const viewId = {
        name: ViewName.MyTasksPage,
        partitionKey: pivot
    };
    const currentFilter = viewStore.getCurrentTaskFilter(viewId);
    const hasActiveDynamicFilter = currentFilter?.filters?.some((f)=>f.field === DynamicFilterField && f.active) === true;
    // Hook to track the view render state of the my tasks page
    useRenderTracker(viewId, calculateMyTasksRenderState);
    const myTasksViewDisplayState = taskStore.getTasksViewDisplayState(viewId);
    useEffect(()=>{
        loadTranslations(appContextStore.getAppContext().locale);
    }, [
        appContextStore
    ]);
    useEffect(()=>{
        ocvActionCreator.logActivity(shouldUseNewListView ? FloodgateActivity.MyTasksNewExperience : FloodgateActivity.MyTasks);
    }, [
        ocvActionCreator,
        shouldUseNewListView
    ]);
    // Keep a session-lifetime latch of whether list view has ever been active.
    // Use an effect so render remains side-effect free.
    useEffect(()=>{
        if (isListView) {
            hasEverBeenListViewInSessionRef.current = true;
        }
    }, [
        isListView
    ]);
    // Log which experience the user lands on (simplified list vs legacy board/grid).
    // Note: useEffect is normally restricted to focus management in this repo, but telemetry-only
    // fire-once logging is acceptable here (consistent with existing useEffects at lines 159/163).
    const hasLoggedEntryRoute = React.useRef(false);
    React.useEffect(()=>{
        if (!hasLoggedEntryRoute.current) {
            hasLoggedEntryRoute.current = true;
            const isNewExperience = shouldUseNewListView;
            // Use the effective rendered viewType — when URL is /view/list but flight is off, user sees the board
            const effectiveViewType = isListView && !isNewMyTasksFlightEnabled ? DefaultViewTypeForPersonalViews : viewType;
            engagementLogger.logEngagement(componentName, EngagementTelemetryConstants.MyTasksEntryRoute, {
                viewType: effectiveViewType,
                isNewExperience: String(isNewExperience)
            });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- fire once on mount only
    const renderBoardView = ()=>{
        // Loading state can be rendered for all pivots
        if (myTasksViewDisplayState === ViewDisplayState.DataNotFetched) {
            return /*#__PURE__*/ React.createElement(TaskBoardViewPlaceHolder, {
                numberOfTaskCardsPerColumn: MyDayMyTaskTaskCardPlaceHolders,
                showAddTaskButtonShimmer: false
            });
        }
        if (myTasksViewDisplayState === ViewDisplayState.DataAvailableButFiltered || myTasksViewDisplayState === ViewDisplayState.DataNotAvailable) {
            return /*#__PURE__*/ React.createElement(Suspense, {
                fallback: /*#__PURE__*/ React.createElement(TaskBoardViewPlaceHolder, {
                    numberOfTaskCardsPerColumn: MyDayMyTaskTaskCardPlaceHolders,
                    showAddTaskButtonShimmer: false
                })
            }, /*#__PURE__*/ React.createElement(LazyEmptyStateView, {
                ...getEmptyStateProps(theme, viewId, myTasksViewDisplayState),
                customStyles: {
                    root: styles.emptyStateRoot
                }
            }));
        }
        return /*#__PURE__*/ React.createElement(Suspense, {
            fallback: /*#__PURE__*/ React.createElement(TaskBoardViewPlaceHolder, {
                numberOfTaskCardsPerColumn: MyDayMyTaskTaskCardPlaceHolders,
                showAddTaskButtonShimmer: false
            })
        }, /*#__PURE__*/ React.createElement(LazyUserTasksBoardView, {
            viewId: viewId,
            isSlimView: false,
            showPlanTitleOnCard: pivot !== MyTasksViewPivotType.FlaggedEmails && pivot !== MyTasksViewPivotType.PrivateTasks,
            taskAccessContext: TaskAccessContext.AssignedToUser,
            mustShowPlanId: mustShowPlanId,
            priorityPlanIds: priorityPlanIds,
            pivot: pivot
        }));
    };
    const renderGridViewByPivot = ()=>{
        const GridView = configProvider().flights.EnableCanvasTaskGrid ? LazyCanvasUserTasksGridView : LazyUserTasksGridView;
        const isDataFinalized = myTasksViewDisplayState !== ViewDisplayState.DataNotFetched;
        const isDataEmpty = myTasksViewDisplayState === ViewDisplayState.DataNotAvailable || myTasksViewDisplayState === ViewDisplayState.DataAvailableButFiltered;
        switch(pivot){
            case MyTasksViewPivotType.PrivateTasks:
                return /*#__PURE__*/ React.createElement(Suspense, {
                    fallback: /*#__PURE__*/ React.createElement(React.Fragment, null)
                }, /*#__PURE__*/ React.createElement(GridView, {
                    viewId: viewId,
                    isSlimView: false,
                    taskAccessContext: TaskAccessContext.AssignedToUser,
                    viewName: GridViewName.PrivateTasksGridView,
                    isCriticalDataReady: isDataFinalized,
                    sortState: null
                }));
            case MyTasksViewPivotType.FlaggedEmails:
                if (!flaggedEmailPlanId || isDataEmpty) {
                    return /*#__PURE__*/ React.createElement(Suspense, {
                        fallback: /*#__PURE__*/ React.createElement(React.Fragment, null)
                    }, /*#__PURE__*/ React.createElement(LazyEmptyStateView, {
                        ...getEmptyStateProps(theme, viewId, myTasksViewDisplayState),
                        customStyles: {
                            root: styles.emptyStateRoot
                        }
                    }));
                }
                return /*#__PURE__*/ React.createElement(Suspense, {
                    fallback: /*#__PURE__*/ React.createElement(React.Fragment, null)
                }, /*#__PURE__*/ React.createElement(GridView, {
                    viewId: viewId,
                    isSlimView: false,
                    taskAccessContext: TaskAccessContext.AssignedToUser,
                    viewName: GridViewName.FlaggedEmailsGridView,
                    isCriticalDataReady: isDataFinalized,
                    sortState: null
                }));
            case MyTasksViewPivotType.All:
                return /*#__PURE__*/ React.createElement(Suspense, {
                    fallback: /*#__PURE__*/ React.createElement(React.Fragment, null)
                }, /*#__PURE__*/ React.createElement(GridView, {
                    viewId: viewId,
                    isSlimView: false,
                    taskAccessContext: TaskAccessContext.AssignedToUser,
                    viewName: GridViewName.PlannerAllUserTasksGridView,
                    isCriticalDataReady: isDataFinalized,
                    sortState: null
                }));
            case MyTasksViewPivotType.AssignedToMe:
            default:
                return /*#__PURE__*/ React.createElement(Suspense, {
                    fallback: /*#__PURE__*/ React.createElement(React.Fragment, null)
                }, /*#__PURE__*/ React.createElement(GridView, {
                    viewId: viewId,
                    isSlimView: false,
                    taskAccessContext: TaskAccessContext.AssignedToUser,
                    viewName: GridViewName.PlannerAssignedToUserGridView,
                    isCriticalDataReady: isDataFinalized,
                    sortState: null
                }));
        }
    };
    /**
     * Render the assigned to user tasks view
     */ const renderView = ()=>{
        switch(viewType){
            case ViewType.Board:
                return renderBoardView();
            case ViewType.Grid:
                return renderGridViewByPivot();
            case ViewType.List:
                // Graceful fallback when URL has /view/list but flight is disabled (e.g., rollback or stale bookmark)
                return renderBoardView();
            default:
                traceLogger.logTrace(0x1e148757 /* tag_4fi3x */ , TraceLevel.Error, `[MyTasksPage] Unhandled viewType ${viewType}`); // should never happen
                return /*#__PURE__*/ React.createElement(React.Fragment, null);
        }
    };
    const newExperienceToggle = isNewMyTasksFlightEnabled ? /*#__PURE__*/ React.createElement("div", {
        className: styles.viewToggle
    }, /*#__PURE__*/ React.createElement(Switch, {
        "data-testid": "my-tasks-page-switch",
        // Keep id undefined once the toggle has ever been on in this session.
        id: !isListView && !hasEverBeenListViewInSessionRef.current ? ElementIds.MyTasksNewExperienceToggle : undefined,
        checked: isListView,
        onChange: handleToggleNewExperience,
        label: Strings.MyTasksPage_NewExperienceToggleLabel,
        "aria-label": Strings.MyTasksPage_NewExperienceToggleLabel
    })) : undefined;
    const messageBars = React.useMemo(()=>{
        const messages = [];
        if (isNewMyTasksFlightEnabled && isStarredExperienceEnabled) {
            messages.push(/*#__PURE__*/ React.createElement(MyTasksStarredMessageBar, {
                key: "mytasks-starred",
                isDevice: configProvider().sessionMetaData.appMetadata.clientType === ClientType.Teams
            }));
        }
        if (hasActiveDynamicFilter) {
            messages.push(/*#__PURE__*/ React.createElement(MyTasksDynamicFilterMessageBar, {
                key: "mytasks-dynamic-filter"
            }));
        }
        return messages;
    }, [
        isNewMyTasksFlightEnabled,
        isStarredExperienceEnabled,
        configProvider,
        hasActiveDynamicFilter
    ]);
    return /*#__PURE__*/ React.createElement(PageLayout, {
        pageId: ViewElementIds.MyTasksView,
        pageTestId: "my-tasks-page",
        customStyles: shouldUseNewListView ? {
            root: mergeClasses(styles.pageLayoutRootForListView, isNarrowContent && styles.pageLayoutRootScrollable)
        } : undefined,
        header: /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(UserViewHeader, {
            title: Strings.MyTasksPage_Title,
            iconElement: /*#__PURE__*/ React.createElement(MyTasksRegular, null),
            trialButton: /*#__PURE__*/ React.createElement(UpsellTrialButtonContainer, {
                entryPoint: TryPremiumEntryPoint.Header
            }),
            showMoreButton: true,
            headerOverflowMenu: /*#__PURE__*/ React.createElement(HeaderOverflowMenuContainer, null),
            pageLinksContent: newExperienceToggle,
            logFloodgateActivity: (activity)=>ocvActionCreator.logActivity(activity),
            shouldDisplayLoadingIndicator: shouldDisplayLoadingIndicator(configProvider, viewLifecycleStore.getViewDataFetchState(viewId))
        })),
        viewTransformBar: /*#__PURE__*/ React.createElement(ViewTransformBarLayout, {
            viewNavigationBar: /*#__PURE__*/ React.createElement(ViewNavigationBarContainer, null),
            viewActionBar: /*#__PURE__*/ React.createElement(ViewActionBarContainer, null)
        }),
        view: shouldUseNewListView ? /*#__PURE__*/ React.createElement("div", {
            ref: listViewContainerRef,
            "data-testid": "list-view-container",
            ...isNarrowContent ? {
                [NARROW_CONTENT_MARKER]: "true"
            } : {},
            className: mergeClasses(styles.listViewContainer, isNarrowContent && styles.listViewContainerNarrow)
        }, messageBars.length > 0 ? /*#__PURE__*/ React.createElement(MessageBarGroup, {
            animate: "both"
        }, messageBars) : null, /*#__PURE__*/ React.createElement(Suspense, {
            fallback: /*#__PURE__*/ React.createElement(TaskBoardViewPlaceHolder, {
                numberOfTaskCardsPerColumn: MyDayMyTaskTaskCardPlaceHolders,
                showAddTaskButtonShimmer: false
            })
        }, /*#__PURE__*/ React.createElement(LazyMyTasksListViewContainer, {
            pivotType: pivot
        }))) : renderView()
    });
};
